import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '@/api/ApiError';
import { D365TextField } from '@/components/D365FormControls';
import {
  getPeople,
  savePerson,
  type PeopleKind,
  type PersonInput,
} from '@/features/people/api/people';

const empty: PersonInput = { name: '', email: '', password: '', passwordConfirmation: '' };

export function PersonFormPage({ kind }: { kind: PeopleKind }) {
  const rawId = useParams().personId;
  const isNew = rawId === 'new';
  const personId = isNew ? null : Number(rawId);
  const singular = kind === 'technicians' ? 'Technician' : 'Manager';
  const query = useQuery({
    queryKey: ['people', kind],
    queryFn: () => getPeople(kind),
    enabled: !isNew && Number.isInteger(personId) && Number(personId) > 0,
  });

  if (isNew) {
    return <PersonEditor kind={kind} personId={null} initial={empty} />;
  }
  if (!Number.isInteger(personId) || Number(personId) <= 0 || query.isError) {
    return <RecordError singular={singular} />;
  }
  if (query.isLoading) {
    return (
      <Box sx={{ p: 4, display: 'grid', placeItems: 'center' }}>
        <CircularProgress aria-label={`Loading ${singular.toLowerCase()}`} />
      </Box>
    );
  }
  const person = query.data?.find((candidate) => candidate.id === personId);
  if (!person) return <RecordError singular={singular} />;

  return (
    <PersonEditor
      key={`${kind}-${person.id}`}
      kind={kind}
      personId={person.id}
      initial={{ name: person.name, email: person.email, password: '', passwordConfirmation: '' }}
    />
  );
}

function PersonEditor({
  kind,
  personId,
  initial,
}: {
  kind: PeopleKind;
  personId: number | null;
  initial: PersonInput;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PersonInput>(initial);
  const [savedForm, setSavedForm] = useState<PersonInput>(initial);
  const isNew = personId === null;
  const basePath = kind === 'technicians' ? '/technicians' : '/managers';
  const singular = kind === 'technicians' ? 'Technician' : 'Manager';
  const save = useMutation({
    mutationFn: () => savePerson(kind, form, personId ?? undefined),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['people', kind] });
      const clean = { ...form, password: '', passwordConfirmation: '' };
      setForm(clean);
      setSavedForm(clean);
      if (isNew) void navigate(`${basePath}/${saved.id}`, { replace: true });
    },
  });
  const saveDisabled =
    !form.name.trim() ||
    !form.email.trim() ||
    (isNew && form.password.length < 8) ||
    form.password !== form.passwordConfirmation ||
    save.isPending || JSON.stringify(form) === JSON.stringify(savedForm);

  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={commandPaperSx}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minHeight: 48, px: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => void navigate(basePath)}
            sx={commandButtonSx}
          >
            Back
          </Button>
          <Button
            startIcon={<SaveOutlinedIcon />}
            disabled={saveDisabled}
            onClick={() => save.mutate()}
            sx={commandButtonSx}
          >
            Save
          </Button>
        </Stack>
      </Paper>
      <Paper elevation={0} sx={{ ...commandPaperSx, mt: 1.5 }}>
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography variant="h1">
            {isNew ? `New ${singular}` : form.name || singular}
            {!isNew && (
              <Typography component="span" color="text.secondary" variant="body2">
                {' '}
                — Saved
              </Typography>
            )}
          </Typography>
          <Typography color="text.secondary">{singular}</Typography>
        </Box>
      </Paper>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 560px) 1fr' },
          gap: 1.5,
          mt: 1.5,
        }}
      >
        <Paper
          component="form"
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            if (!saveDisabled) save.mutate();
          }}
          elevation={0}
          sx={{ ...commandPaperSx, p: 2 }}
        >
          <Typography fontWeight={600} sx={{ mb: 2 }}>
            {singular.toUpperCase()} INFORMATION
          </Typography>
          <Stack spacing={2}>
            <D365TextField
              label="Name"
              name={`${kind}-new-name`}
              autoComplete="off"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
            />
            <D365TextField
              type="email"
              label="Email"
              name={`${kind}-new-email`}
              autoComplete="off"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              required
            />
            <D365TextField
              type="password"
              label={isNew ? 'Password' : 'New password (optional)'}
              name={`${kind}-new-password`}
              autoComplete="new-password"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              required={isNew}
            />
            <D365TextField
              type="password"
              label="Confirm password"
              name={`${kind}-confirm-new-password`}
              autoComplete="new-password"
              value={form.passwordConfirmation}
              onChange={(event) => setForm({ ...form, passwordConfirmation: event.target.value })}
              required={Boolean(form.password)}
            />
            {save.isError && <Alert severity="error">{personSaveError(save.error)}</Alert>}
          </Stack>
        </Paper>
        <Paper elevation={0} sx={{ ...commandPaperSx, p: 2, alignSelf: 'start' }}>
          <Typography fontWeight={600}>Security</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {isNew
              ? 'A password of at least eight characters is required for a new account.'
              : 'Leave the password fields empty to keep the existing password.'}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}

function personSaveError(error: Error) {
  if (error instanceof ApiError && error.details && typeof error.details === 'object') {
    const details = error.details as { errors?: Record<string, unknown> };
    const messages = Object.values(details.errors ?? {}).flatMap((value) =>
      Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [],
    );
    if (messages.length) return messages.join(' ');
  }
  return error.message || 'The user could not be saved.';
}

function RecordError({ singular }: { singular: string }) {
  return (
    <Alert severity="error" sx={{ m: 2 }}>
      This {singular.toLowerCase()} record could not be loaded.
    </Alert>
  );
}

const commandPaperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};


